/**
 * Rule-suggestion derivation for the Work Log Review Classify modal — task 159d.
 *
 * Pure function. Inputs: the active group-by dimensions, the optional group
 * key (when scope = 'group'), and a sample of in-scope events. Output:
 *   - `DerivedRule` — a single condition the modal pre-fills the Step-2 form
 *     with (per locked decision #10 in tasks/159-work-log-review-parent.md).
 *   - `{ kind: 'fallback-app', suggestedApp }` — group-by `appFileName` is
 *     not engine-evaluable; the modal offers a derived `app`-based rule
 *     instead (most common `app` value across the events sample).
 *   - `null` — no rule is derivable. The modal renders the "manual override
 *     only" branch.
 *
 * Engine-evaluable fields (see `web/src/classification/rule-schema.ts`):
 *   `app | title | url | site | ip | document`. Group-by dimensions outside
 *   that list are skipped (`employee`, `businessUnit`, `project`, `client`,
 *   `classificationSource`, `classifiedByRule`, `day`, `hour`, `weekday`).
 *   `rootDomain` is mapped to `url` because the engine doesn't evaluate
 *   `rootDomain` directly; substring on `url` is the closest equivalent.
 */

import type { GroupByDimension, WorkLogReviewEventRow } from '@/types/workLogReview';

export type RuleField = 'app' | 'title' | 'url' | 'site' | 'ip' | 'document';

export interface DerivedRule {
  field: RuleField;
  value: string;
}

export interface FallbackAppSuggestion {
  kind: 'fallback-app';
  /** Most common `app` value across `events`. Empty string if none. */
  suggestedApp: string;
}

export type RuleSuggestion = DerivedRule | FallbackAppSuggestion | null;

interface DeriveArgs {
  groupBy: readonly GroupByDimension[];
  groupKey?: Record<string, string | null>;
  events: readonly WorkLogReviewEventRow[];
}

// Preference order: when multiple engine-evaluable dimensions are stacked,
// `app` is the most actionable, then text fields, then network/document.
// This drives both the stacked-group-by case and the multi-select common-
// denominator case.
const PREFERENCE_ORDER: readonly RuleField[] = ['app', 'title', 'url', 'site', 'ip', 'document'];

const DIMENSION_TO_FIELD: Partial<Record<GroupByDimension, RuleField>> = {
  app: 'app',
  title: 'title',
  url: 'url',
  site: 'site',
  // Engine inspects `url` not `rootDomain` — substring on the url is the
  // closest match for "events from this domain".
  rootDomain: 'url',
};

function derivedFromKey(
  dim: GroupByDimension,
  groupKey: Record<string, string | null>,
): DerivedRule | null {
  const field = DIMENSION_TO_FIELD[dim];
  if (!field) return null;
  const value = groupKey[dim];
  if (value === null || value === undefined || value === '') return null;
  return { field, value };
}

function mostCommonApp(events: readonly WorkLogReviewEventRow[]): string {
  const counts = new Map<string, number>();
  for (const e of events) {
    if (e.app) counts.set(e.app, (counts.get(e.app) ?? 0) + 1);
  }
  let best = '';
  let bestCount = 0;
  for (const [app, count] of counts) {
    if (count > bestCount) {
      best = app;
      bestCount = count;
    }
  }
  return best;
}

function readField(e: WorkLogReviewEventRow, field: RuleField): string | null {
  switch (field) {
    case 'app':
      return e.app;
    case 'title':
      return e.title;
    case 'url':
      return e.url;
    case 'site':
      return e.site;
    case 'ip':
    case 'document':
      // Neither lives on the event row — events carry app/title/url/site
      // and that's it. Both `ip` and `document` are derivable only at
      // ingest by the desktop agent, so the modal cannot common-denominator
      // them from a sample. Return null → the field is skipped.
      return null;
  }
}

function commonDenominator(events: readonly WorkLogReviewEventRow[]): DerivedRule | null {
  if (events.length === 0) return null;
  for (const field of PREFERENCE_ORDER) {
    const first = readField(events[0], field);
    if (first === null || first === '') continue;
    let allMatch = true;
    for (let i = 1; i < events.length; i += 1) {
      if (readField(events[i], field) !== first) {
        allMatch = false;
        break;
      }
    }
    if (allMatch) return { field, value: first };
  }
  return null;
}

/**
 * See file header for semantics. The function never reads context or config —
 * inputs in, suggestion out.
 */
export function deriveRuleSuggestion(args: DeriveArgs): RuleSuggestion {
  const { groupBy, groupKey, events } = args;

  if (groupBy.length === 0) {
    // No group-by → multi-select scope. Find the narrowest single-field
    // common denominator across the events sample.
    return commonDenominator(events);
  }

  if (!groupKey) {
    // Group scope but no key — caller bug; fall back to the events sample.
    return commonDenominator(events);
  }

  // Single dimension fast-path: `app`, `title`, `url`, `site`, `rootDomain`,
  // `appFileName`. Anything else (`employee`, `day`, …) is non-engine.
  if (groupBy.length === 1) {
    const dim = groupBy[0];
    if (dim === 'appFileName') {
      return { kind: 'fallback-app', suggestedApp: mostCommonApp(events) };
    }
    return derivedFromKey(dim, groupKey);
  }

  // Stacked group-by: pick the first engine-evaluable dimension in the
  // preference order. If only `appFileName` is engine-adjacent, surface the
  // fallback-app suggestion. If nothing matches, derive from events.
  const engineDimsWithFields: Array<{ dim: GroupByDimension; field: RuleField }> = [];
  for (const dim of groupBy) {
    const field = DIMENSION_TO_FIELD[dim];
    if (field) engineDimsWithFields.push({ dim, field });
  }
  if (engineDimsWithFields.length > 0) {
    // Order by PREFERENCE_ORDER (via field).
    engineDimsWithFields.sort(
      (a, b) => PREFERENCE_ORDER.indexOf(a.field) - PREFERENCE_ORDER.indexOf(b.field),
    );
    for (const { dim } of engineDimsWithFields) {
      const derived = derivedFromKey(dim, groupKey);
      if (derived) return derived;
    }
    // Every engine dim had a null key — fall through to the events sample.
  }

  if (groupBy.includes('appFileName')) {
    return { kind: 'fallback-app', suggestedApp: mostCommonApp(events) };
  }

  return commonDenominator(events);
}
